import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface HomePageProps {
  onNextStep: (serviceType: 'simple' | 'complete') => void;
  loading: boolean;
}

export default function HomePage({ onNextStep, loading }: HomePageProps) {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState<'simple' | 'complete' | null>('simple');

  const handleStart = () => {
    if (selectedOption && !loading) {
      onNextStep(selectedOption);
    }
  };

  return (
    <div className="flex flex-col items-center px-4 py-6 flex-grow bg-[#ededed]">
      <div className="flex flex-col items-center justify-between w-full h-full max-w-md mx-auto">
        <div className="text-center align-center">
          <h1 className="text-3xl sm:text-3xl font-extralight leading-tight mb-2">
            {t('home.title')}
          </h1>
          <h2 className="text-lg sm:text-xl w-[80%] mx-auto font-extralight">
            {t('home.subtitle')}
          </h2>
        </div>
        
        <div className="w-full grid grid-cols-2 gap-4 mb-6">
          {/* Simple Oil Change */}
          <div 
            className={`flex flex-col items-center flex-grow ${loading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`} 
            onClick={() => !loading && setSelectedOption('simple')}
          >
            <div className="relative w-42 h-42 sm:w-32 sm:h-32">
              <img 
                src="/simple_videnge.png" 
                alt="Simple Oil Change"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-center text-lg sm:text-base mb-2 font-bold">{t('home.simpleOilChange')}</h3>
            <div className="flex flex-col items-center flex-grow">
              <div className="text-center text-xs sm:text-sm text-[#9e9e9e] space-y-1 mb-2">
                <p className="font-light">{t('home.engineOil')}</p>
                <p className="font-light">{t('home.oilFilter')}</p>
              </div>
              <button 
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-white border-4 transition-colors ${
                  selectedOption === 'simple' 
                    ? 'bg-[#2196F3]' 
                    : 'bg-white'
                }`}
                aria-label="Select Simple Oil Change"
                disabled={loading}
              />
            </div>
          </div>

          {/* Complete Oil Change */}
          <div 
            className={`flex flex-col items-center flex-grow ${loading ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`} 
            onClick={() => !loading && setSelectedOption('complete')}
          >
            <div className="relative w-42 h-42 sm:w-32 sm:h-32">
              <img 
                src="/complet_videnge.png" 
                alt="Complete Oil Change"
                className="w-full h-full object-contain"
              />
            </div>
            <h3 className="text-center text-lg sm:text-base mb-2 font-bold">{t('home.completeOilChange')}</h3>
            <div className="flex flex-col items-center">
              <div className="text-center text-xs sm:text-sm text-[#9e9e9e] space-y-1 mb-2">
                <p className="font-light">{t('home.engineOil')}</p>
                <p className="font-light">{t('home.oilFilter')}</p>
                <p className="font-light">{t('home.fuelFilter')}</p>
                <p className="font-light">{t('home.airFilter')}</p>
              </div>
              <button 
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border border-white border-4 mt-auto transition-colors ${
                  selectedOption === 'complete' 
                    ? 'bg-[#2196F3]' 
                    : 'bg-white'
                }`}
                aria-label="Select Complete Oil Change"
                disabled={loading}
              />
            </div>
          </div>
        </div>
        <button 
          className={`w-[90%] py-4 rounded-full border-[#2196F3] border-2 font-bold bg-white text-[#2196F3] shadow-md text-lg mt-2 ${
            loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'
          }`}
          onClick={handleStart}
          disabled={!selectedOption || loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-[#2196F3]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t('common.loading')}
            </div>
          ) : (
            t('home.startButton')
          )}
        </button>
      </div>
    </div>
  );
}